import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueries, useQuery } from "react-query";
import { queryKeys } from "../../api/query";
import { Panel } from "primereact/panel";
import { usePeopleDetail } from "../../api/queries/people/usePeopleDetail";
import { ImportData, PeopleVisibility } from "../../api/types/people";
import {
  Dropdown,
  DropdownChangeEvent,
  DropdownProps,
} from "primereact/dropdown";
import { useSkills } from "../../api/queries/skills/useSkills";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { Checkbox } from "primereact/checkbox";
import { useJourneyTypes } from "../../api/queries/enum/useJourneyTypes";
import { Nullable } from "primereact/ts-helpers";
import moment from "moment";
import { classNames } from "primereact/utils";
import { useCampusActivities } from "../../api/queries/enum/useCampusActivities";
import { useFields } from "../../api/queries/enum/useFields";
import commonStyles from "../common.module.css";
import { linkListRequest } from "../../api/queries/links/list";
import { Links } from "./link";
import { LinkSectionEnum } from "../../api/types/link";
import { TagListItem, TagListResponseData } from "../../api/types/tag";
import { tagListRequest } from "../../api/queries/tag/list";
import ImportDataViewer from "./importDataViewer";
import styles from "./styles.module.css";

export default function PeopleView() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const visibilityOptions: VisibilityOption[] = [
    { value: "full", label: "Full" },
    { value: "without_contact", label: "Without contact" },
    { value: "none", label: "None" },
  ];

  const { data: peopleResponse } = usePeopleDetail(uuid);
  const { data: skillsResponse } = useSkills();

  // -----------------------------------------------------------------------------------------------

  const { data: journeyTypesResponse } = useJourneyTypes();
  const { data: campusActivitiesResponse } = useCampusActivities();
  const { data: fieldsResponse } = useFields();

  const { data: profileLinks } = useQuery({
    queryFn: () => linkListRequest(LinkSectionEnum.PROFILE, uuid),
    queryKey: queryKeys.links(LinkSectionEnum.PROFILE, uuid),
    enabled: Boolean(uuid),
  });

  const profileEducations = peopleResponse?.data.education || [];

  const educationQueries = useQueries(
    profileEducations.map((education) => {
      return {
        queryFn: () => linkListRequest(LinkSectionEnum.EDUCATION, education.id),
        queryKey: queryKeys.links(LinkSectionEnum.EDUCATION, education.id),
      };
    }),
  );

  const profileJobs = peopleResponse?.data.jobs || [];

  const jobQueries = useQueries(
    profileJobs.map((job) => {
      return {
        queryFn: () => linkListRequest(LinkSectionEnum.JOB, job.id),
        queryKey: queryKeys.links(LinkSectionEnum.JOB, job.id),
      };
    }),
  );

  const [errors] = useState<string[]>([]);

  const [firstName, setFirstName] = useState<string>("");

  const [lastName, setLastName] = useState<string>("");

  const [birthLastName] = useState<string>("");

  const [email, setEmail] = useState<string>("");

  const [needs, setNeeds] = useState<string>("");

  const [bio, setBio] = useState<string>("");

  const [visibility, setVisibility] = useState<PeopleVisibility | null>(null);
  const [skills, setSkills] = useState<string[]>([]);

  // --- hometown ----------------------------------------------------------------------------------
  const [hometown] = useState<string>("");

  // --- current location --------------------------------------------------------------------------

  const [currentLocation] = useState<string>("");

  const [linkedin] = useState<string>("");

  const [videoUrl] = useState<string>("");
  const [openToChat, setOpenToChat] = useState<boolean>(false);
  const [importData, setImportData] = useState<ImportData[]>([]);
  const [showSidebar, setShowSidebar] = useState(false);

  const [hiring, setHiring] = useState<boolean>(false);
  const [hiringNote, setHiringNote] = useState<string | null>(null);

  const [education, setEducation] = useState<EducationValue[]>([]);
  const [tags, setTags] = useState<TagListItem[]>();
  useEffect(() => {
    tagListRequest().then((data: TagListResponseData) =>
      setTags(data.data.results || []),
    );
  }, []);
  const onEducationJourneyTypeIdChange = (
    index: number,
    journeyTypeId: string,
  ) => {
    setEducation(
      education.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { journey_type_id: journeyTypeId });
        }

        return item;
      }),
    );
  };

  const onEducationFieldsChange = (index: number, fields: string[]) => {
    setEducation(
      education.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, {
            fields,
          });
        }

        return item;
      }),
    );
  };

  const [jobs, setJobs] = useState<JobValue[]>([]);

  const onJobJourneyTypeIdChange = (index: number, journeyTypeId: string) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { journey_type_id: journeyTypeId });
        }

        return item;
      }),
    );
  };

  const onJobFieldIdChange = (index: number, fieldId: string) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { field_id: fieldId });
        }

        return item;
      }),
    );
  };

  useEffect(() => {
    if (peopleResponse) {
      const people = peopleResponse.data;

      setFirstName(people.first_name);
      setLastName(people.last_name);
      setEmail(people.email || "");
      setVisibility(people.visibility);
      setSkills(people.skills.map((skill) => skill.id));
      setNeeds(people.needs);
      setBio(people.bio);
      setOpenToChat(people.open_to_chat);
      setImportData(people.import_data);
      setHiring(people.hiring);
      setHiringNote(people.hiring_note);

      const peopleEducation: EducationValue[] = people.education.map((item) => {
        const value: EducationValue = {
          id: item.id,
          finished: item.finished,
          institution: item.institution,
          journey_type_id: item.journey_type?.id || "",
          started_at: moment(item.started_at).toDate(),
          finished_at: item.finished_at
            ? moment(item.finished_at).toDate()
            : null,
          campus_activities: item.campus_activities.map(
            (campusActivity) => campusActivity.id,
          ),
          fields: item.fields.map((field) => field.id),
          note: item.note || "",
          rating: item.rating.rating,
          rating_note: item.rating.note || "",
        };

        return value;
      });

      setEducation(peopleEducation);

      const peopleJobs: JobValue[] = people.jobs.map((item) => {
        const value: JobValue = {
          id: item.id,
          finished: item.finished || false,
          position: item.position,
          institution: item.institution,
          journey_type_id: item.journey_type?.id || "",
          started_at: moment(item.started_at).toDate(),
          finished_at: item.finished_at
            ? moment(item.finished_at).toDate()
            : null,
          field_id: item.field_id || "",
          note: item.note || "",
          rating: item.rating.rating,
          rating_note: item.rating.note || "",
        };

        return value;
      });

      setJobs(peopleJobs);
    }
  }, [peopleResponse]);

  const onCancel = () => navigate("/people");

  const [careerGroupedFields, setCareerGroupedFields] = useState<
    GroupedFieldItem[]
  >([]);

  useEffect(() => {
    if (fieldsResponse) {
      const careerFields = fieldsResponse.data.results.filter(
        (field) => field.type === "career",
      );

      const levelOne = careerFields.filter((field) => field.parent === null);

      const levelTwo = careerFields.filter(
        (field) => field.parent !== null && field.parent.parent === null,
      );

      const levelThree = careerFields.filter(
        (field) => field.parent !== null && field.parent.parent !== null,
      );

      const careerGroupedFields: GroupedFieldItem[] = [];

      levelOne.forEach((levelOneField) => {
        careerGroupedFields.push({
          label: levelOneField.name,
          value: levelOneField.id,
          level: 1,
        });

        levelTwo
          .filter(
            (levelTwoField) => levelTwoField.parent?.id === levelOneField.id,
          )
          .forEach((levelTwoField) => {
            careerGroupedFields.push({
              label: levelTwoField.name,
              value: levelTwoField.id,
              level: 2,
            });

            levelThree
              .filter(
                (levelThreeField) =>
                  levelThreeField.parent?.id === levelTwoField.id,
              )
              .forEach((levelThreeField) => {
                careerGroupedFields.push({
                  label: levelThreeField.name,
                  value: levelThreeField.id,
                  level: 3,
                });
              });
          });
      });

      setCareerGroupedFields(careerGroupedFields);
    }
  }, [fieldsResponse]);

  const careerFieldValueTemplate = (
    option: GroupedFieldItem,
    props: DropdownProps,
  ) => {
    if (option) {
      return (
        <div className="flex align-items-center">
          <div>{option.label}</div>
        </div>
      );
    }

    return <div>&nbsp;</div>;
  };

  const careerFieldItemTemplate = (option: GroupedFieldItem) => {
    return (
      <div
        style={{ paddingLeft: `${option.level}rem` }}
        className={classNames({
          "font-bold": option.level === 1,
          "font-italic": option.level === 2,
        })}
      >
        <div>{option.label}</div>
      </div>
    );
  };

  const [educationGroupedFields, setEducationGroupedFields] = useState<
    GroupedField[]
  >([]);

  useEffect(() => {
    if (fieldsResponse) {
      const educationFields = fieldsResponse.data.results.filter(
        (field) => field.type === "education",
      );
      const educationParentsFields = educationFields.filter(
        (field) => field.parent === null,
      );

      const educationGroupedFields: GroupedField[] = [];

      educationParentsFields.forEach((parent) => {
        educationGroupedFields.push({
          label: parent.name,
          items: educationFields
            .filter((field) => field.parent?.id === parent.id)
            .map((field) => ({
              label: field.name,
              value: field.id,
              level: 1,
            })),
        });
      });

      setEducationGroupedFields(educationGroupedFields);
    }
  }, [fieldsResponse]);

  const educationFieldItemTemplate = (option: GroupedFieldItem) => {
    return (
      <div className={"pl-5"}>
        <div>{option.label}</div>
      </div>
    );
  };

  const [groupedCampusActivities, setGroupedCampusActivities] = useState<
    GroupedCampusActivity[]
  >([]);

  useEffect(() => {
    if (campusActivitiesResponse) {
      let campusActivities = campusActivitiesResponse.data.results;

      const campusActivityParents = campusActivities.filter(
        (campusActivityItem) => campusActivityItem.parent === null,
      );

      const groupedCampusActivities: GroupedCampusActivity[] = [];

      campusActivityParents.forEach((parent) => {
        groupedCampusActivities.push({
          label: parent.name,
          items: campusActivities
            .filter((campusActivity) => campusActivity.parent?.id === parent.id)
            .map((campusActivity) => ({
              label: campusActivity.name,
              value: campusActivity.id,
            })),
        });
      });

      setGroupedCampusActivities(groupedCampusActivities);
    }
  }, [campusActivitiesResponse]);

  return (
    <div className={styles["form-with-sidebar-wrapper"]}>
      <div
        style={{
          marginRight: importData && window.innerWidth >= 768 ? "30rem" : 0,
        }}
      >
        <form onSubmit={(e) => e.preventDefault()} className="col-12">
          <Panel header="People" toggleable={true} className="mb-3">
            <div className="p-fluid">
              <div className="grid formgrid pt-3">
                <div className="field col-12 md:col-3 ">
                  <div className="p-float-label mt-3">
                    <InputText value={firstName} disabled />
                    <label>First Name *</label>
                  </div>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText value={lastName} disabled />
                    <label>Last Name *</label>
                  </div>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText value={birthLastName} disabled />
                    <label>Birth Last Name</label>
                  </div>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText value={email} disabled />
                    <label>Email</label>
                  </div>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText value={needs} disabled />
                    <label>Needs *</label>
                  </div>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText
                      value={
                        visibilityOptions.find(
                          (option) => option.value === visibility,
                        )?.label || ""
                      }
                      disabled
                      className={classNames({
                        "p-invalid": errors.indexOf("visibility") > -1,
                      })}
                    />
                    <label>Visibility *</label>
                  </div>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText
                      value={
                        skillsResponse?.data.results.find(
                          (skill) => skill.id === skills[0],
                        )?.name || ""
                      }
                      disabled
                      className={classNames({
                        "p-invalid": errors.indexOf("skills") > -1,
                      })}
                    />
                    <label>Skills</label>
                  </div>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText value={hometown} disabled />
                    <label>Hometown</label>
                  </div>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText value={currentLocation} disabled />
                    <label>Current ZIP Code</label>
                  </div>
                  <small className={commonStyles.fieldHelp}>
                    Use format 12345 or 12345-6789.
                  </small>
                </div>

                <div className="field col-12 field-checkbox md:col-3">
                  <Checkbox
                    checked={openToChat}
                    disabled={true}
                    inputId="open-to-chat"
                  />
                  <label htmlFor="open-to-chat">Open to chat</label>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText value={linkedin} disabled />
                    <label>Linkedin</label>
                  </div>
                </div>

                <div className="field col-12 md:col-3">
                  <div className="p-float-label mt-3">
                    <InputText value={videoUrl} disabled />
                    <label>Article URL</label>
                  </div>
                </div>

                <div className="field col-12 field-checkbox md:col-3">
                  <Checkbox checked={hiring} disabled={true} inputId="hiring" />
                  <label htmlFor="hiring">Hiring</label>
                </div>

                <div className="field col-12">
                  <div className="mt-3">
                    <div className="p-panel-header">
                      <span className="p-panel-title">Bio</span>
                      <span className="p-panel-icons"></span>
                    </div>
                    <div
                      dangerouslySetInnerHTML={{ __html: hiringNote ?? "" }}
                      className="formatted-text"
                    />
                  </div>
                </div>

                <div className="field col-12">
                  <div className="mt-3">
                    {bio && bio.length === 0 && errors.indexOf("bio") > -1 && (
                      <i className="text-red-500">No bio provided.</i>
                    )}
                    <div className="p-panel-header">
                      <span className="p-panel-title">Bio</span>
                      <span className="p-panel-icons"></span>
                    </div>
                    <div
                      dangerouslySetInnerHTML={{ __html: bio }}
                      className="formatted-text"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  label="Show Imported Data"
                  onClick={() => setShowSidebar(true)}
                  className={`${styles["mobile-only-button"]} p-button-sm mb-2`}
                />

                {importData && (showSidebar || window.innerWidth >= 768) && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      right: 0,
                      height: "100vh",
                      width: window.innerWidth < 768 ? "18rem" : "30rem",
                      overflowY: "auto",
                      padding: "1rem",
                      backgroundColor: "#fff",
                      borderLeft: "1px solid #ccc",
                      zIndex: 1000,
                    }}
                  >
                    <Button
                      type="button"
                      icon="pi pi-times"
                      className={`${styles["mobile-only-button"]} p-button-text p-button-sm`}
                      onClick={() => setShowSidebar(false)}
                    />
                    <h2>Imported Data from Tally</h2>
                    <ImportDataViewer importData={importData} />
                  </div>
                )}
                {uuid && (
                  <div className="field col-12">
                    <Links
                      section={LinkSectionEnum.PROFILE}
                      uuid={uuid}
                      links={profileLinks?.data.results || []}
                      availableTags={tags || []}
                    />
                  </div>
                )}
              </div>
            </div>
          </Panel>

          <Panel
            header="Education"
            toggleable={true}
            className="mb-3"
            headerTemplate={() => (
              <div className="p-panel-header">
                <span className="p-panel-title">Education</span>
                <span className="p-panel-icons"></span>
              </div>
            )}
          >
            <div className="p-fluid">
              {education.length === 0 && (
                <i
                  className={classNames({
                    "text-red-500": errors.indexOf("education") > -1,
                  })}
                >
                  No educations.
                </i>
              )}

              {education.map((item, i) => (
                <>
                  <div key={i} className="grid formgrid pt-3">
                    <div className="col-12">
                      <div className="flex align-items-center mb-3">
                        <strong className="mr-3">Education #{i + 1}</strong>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3 ">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={
                            item.started_at
                              ? moment(item.started_at).format("YYYY-MM-DD")
                              : "N/A"
                          }
                          disabled
                        />
                        <label>Started at *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3 ">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={
                            item.finished_at
                              ? moment(item.finished_at).format("YYYY-MM-DD")
                              : "N/A"
                          }
                          disabled
                        />
                        <label>Finished at</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={item.institution?.name || ""}
                          disabled
                        />
                        <label>Institution *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <Dropdown
                          value={item.journey_type_id}
                          onChange={(event: DropdownChangeEvent) =>
                            onEducationJourneyTypeIdChange(i, event.value)
                          }
                          options={journeyTypesResponse?.data.results || []}
                          optionLabel="name"
                          optionValue="id"
                          className={classNames({
                            "p-invalid":
                              errors.indexOf(
                                `education[${i}].journey_type_id`,
                              ) > -1,
                          })}
                          filter={true}
                          filterBy="name"
                        />
                        <label>Context *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="mt-3">
                        <label>Campus activity</label>
                        <div>
                          {groupedCampusActivities
                            .flatMap((group) => group.items)
                            .filter((activity) =>
                              item.campus_activities.includes(activity.value),
                            )
                            .map((activity) => (
                              <span key={activity.value} className="block">
                                {activity.label}
                              </span>
                            ))}
                        </div>
                      </div>
                    </div>

                    <div className="field-checkbox col-12 md:col-3 ">
                      <Checkbox
                        checked={item.finished}
                        disabled={true}
                        inputId={`finished-${i}`}
                      />
                      <label htmlFor={`finished-${i}`}>Finished</label>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <MultiSelect
                          value={item.fields}
                          onChange={(event: MultiSelectChangeEvent) =>
                            onEducationFieldsChange(i, event.value)
                          }
                          options={educationGroupedFields || []}
                          optionLabel="label"
                          optionValue="value"
                          optionGroupLabel="label"
                          optionGroupChildren="items"
                          itemTemplate={educationFieldItemTemplate}
                          className={classNames({
                            "p-invalid":
                              errors.indexOf(`education[${i}].fields`) > -1,
                          })}
                          filter={true}
                          filterBy="label"
                        />
                        <label>Education field *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={
                            item.rating !== null
                              ? item.rating.toString()
                              : "N/A"
                          }
                          disabled
                        />
                        <label>Rating</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText value={item.rating_note} disabled />
                        <label>Rating note</label>
                      </div>
                    </div>

                    <div className="field col-12">
                      <div className="mt-3">
                        <label>Education note</label>
                        <div
                          dangerouslySetInnerHTML={{ __html: item.note }}
                          className="formatted-text"
                        />
                      </div>
                    </div>
                  </div>

                  {uuid &&
                    education.map(
                      (edu, i) =>
                        edu.id && (
                          <Links
                            section={LinkSectionEnum.EDUCATION}
                            uuid={item.id as string}
                            links={
                              educationQueries?.[i]?.data?.data.results || []
                            }
                            availableTags={tags || []}
                          />
                        ),
                    )}
                </>
              ))}
            </div>
          </Panel>

          <Panel
            header="Careers"
            toggleable={true}
            className="mb-3"
            headerTemplate={() => (
              <div className="p-panel-header">
                <span className="p-panel-title">Careers</span>
                <span className="p-panel-icons"></span>
              </div>
            )}
          >
            <div className="p-fluid">
              {jobs.length === 0 && (
                <i
                  className={classNames({
                    "text-red-500": errors.indexOf("jobs") > -1,
                  })}
                >
                  No careers.
                </i>
              )}

              {jobs.map((item, i) => (
                <>
                  <div key={i} className="grid formgrid pt-3">
                    <div className="col-12">
                      <div className="flex align-items-center mb-3">
                        <strong className="mr-3">Career #{i + 1}</strong>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3 ">
                      <div className="p-float-label mt-3">
                        <InputText value={item.position} disabled />
                        <label>Position *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3 ">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={
                            item.started_at
                              ? moment(item.started_at).format("YYYY-MM-DD")
                              : "N/A"
                          }
                          disabled
                        />
                        <label>Started at *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3 ">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={
                            item.finished_at
                              ? moment(item.finished_at).format("YYYY-MM-DD")
                              : "N/A"
                          }
                          disabled
                        />
                        <label>Finished at</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={item.institution?.name || ""}
                          disabled
                        />
                        <label>Institution *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <Dropdown
                          value={item.journey_type_id}
                          onChange={(event: DropdownChangeEvent) =>
                            onJobJourneyTypeIdChange(i, event.value)
                          }
                          options={journeyTypesResponse?.data.results || []}
                          optionLabel="name"
                          optionValue="id"
                          className={classNames({
                            "p-invalid":
                              errors.indexOf(`jobs[${i}].journey_type_id`) > -1,
                          })}
                          filter={true}
                          filterBy="name"
                        />
                        <label>Context *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <Dropdown
                          value={item.field_id}
                          onChange={(event: DropdownChangeEvent) =>
                            onJobFieldIdChange(i, event.value)
                          }
                          options={careerGroupedFields || []}
                          optionLabel="label"
                          optionValue="value"
                          // optionGroupLabel="label"
                          // optionGroupChildren="items"
                          itemTemplate={careerFieldItemTemplate}
                          valueTemplate={careerFieldValueTemplate}
                          className={classNames({
                            "p-invalid":
                              errors.indexOf(`jobs[${i}].field_id`) > -1,
                          })}
                          filter={true}
                          filterBy="label"
                        />
                        <label>Career field *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={
                            item.rating !== null
                              ? item.rating.toString()
                              : "N/A"
                          }
                          disabled
                        />
                        <label>Rating</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText value={item.rating_note} disabled />
                        <label>Rating note</label>
                      </div>
                    </div>

                    <div className="field-checkbox col-12 md:col-3 ">
                      <Checkbox
                        checked={item.finished}
                        disabled={true}
                        inputId={`job-finished-${i}`}
                      />
                      <label htmlFor={`job-finished-${i}`}>Finished</label>
                    </div>

                    <div className="field col-12">
                      <div className="mt-3">
                        <div
                          dangerouslySetInnerHTML={{ __html: item.note }}
                          className="formatted-text"
                        />
                      </div>
                    </div>
                  </div>

                  {uuid && item.id && (
                    <Links
                      section={LinkSectionEnum.JOB}
                      uuid={item.id as string}
                      links={jobQueries?.[i]?.data?.data.results || []}
                      availableTags={tags || []}
                    />
                  )}
                </>
              ))}
            </div>
          </Panel>

          <div className="flex gap-1 justify-content-end">
            <Button
              type="button"
              label="Cancel"
              className="p-button-sm p-button-secondary"
              text={true}
              onClick={onCancel}
              disabled={true}
            />
          </div>
        </form>
      </div>
    </div>
  );
}

interface VisibilityOption {
  label: string;
  value: PeopleVisibility;
}

interface EducationValue {
  id?: string;
  finished: boolean;
  started_at: Nullable<Date>;
  finished_at: Nullable<Date>;
  institution: { id: string; name: string } | null;
  journey_type_id: string;
  campus_activities: string[];
  fields: string[];
  note: string;
  rating: number | null;
  rating_note: string;
}

interface JobValue {
  id?: string;
  finished: boolean;
  started_at: Nullable<Date>;
  finished_at: Nullable<Date>;
  institution: { id: string; name: string } | null;
  journey_type_id: string;
  position: string;
  field_id: string;
  note: string;
  rating: number | null;
  rating_note: string;
}

interface GroupedFieldItem {
  label: string;
  value: string;
  level: number;
}

interface GroupedField {
  label: string;
  items: GroupedFieldItem[];
}

interface GroupedCampusActivityItem {
  label: string;
  value: string;
}

interface GroupedCampusActivity {
  label: string;
  items: GroupedCampusActivityItem[];
}
